import { memo } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { styled } from "@mui/material";
import { LISTBOX_PADDING_PX } from "../constants";

// Mirrors Autocomplete's own GroupLabel, which MUI does not export.
const GroupLabel = styled(ListSubheader, { name: "MuiAutocomplete", slot: "GroupLabel" })(({ theme }) => ({
  top: -LISTBOX_PADDING_PX,
  backgroundColor: (theme.vars || theme).palette.background.paper,

  [`&.${autocompleteClasses.groupLabel}`]: {
    fontWeight: 500,
  },
})) as typeof ListSubheader;

// A custom label has no ListSubheader to make it sticky, so this does.
const CustomGroupLabel = styled("div", { name: "MuiAutocomplete", slot: "GroupLabel" })(({ theme }) => ({
  position: "sticky",
  top: -LISTBOX_PADDING_PX,
  zIndex: 1,
  backgroundColor: (theme.vars || theme).palette.background.paper,

  [`&.${autocompleteClasses.groupLabel}`]: {
    fontWeight: 500,
  },
}));

interface ListboxGroupHeaderProps {
  /** Referenced by the group this header names. */
  id: string;
  label: string;
  index: number;
  measureElement?: (node: Element | null) => void;
  renderLabel?: (group: string) => React.ReactNode;
}

/**
 * The header of one group, rendered first inside the group's item and pinned
 * while its group is in view, as Autocomplete's own group label is.
 */
function ListboxGroupHeader({ id, label, index, measureElement, renderLabel }: ListboxGroupHeaderProps) {
  if (renderLabel) {
    return (
      <CustomGroupLabel id={id} ref={measureElement} data-index={index} className={autocompleteClasses.groupLabel}>
        {renderLabel(label)}
      </CustomGroupLabel>
    );
  }

  return (
    <GroupLabel
      id={id}
      ref={measureElement}
      data-index={index}
      component="div"
      className={autocompleteClasses.groupLabel}
    >
      {label}
    </GroupLabel>
  );
}

export default memo(ListboxGroupHeader);
